import clsx from "clsx";
import { useState } from "react";
import { AccordionDetails, AccordionSummary, Accordion } from "@mui/material";
import { useRouter } from "next/router";
import Icon from "@Components/ui/icon";
import { IFilterItem } from "@Types/SearchUtils";
import { ISearchSliceInitialState } from "@Types/search-page";
import { useAppSelector } from "@wff/store/hooks";
import {
  MOBILE_DRAWER_MENU_TEST_ID,
  CLOSE_DRAWER_BUTTON_TEST_ID,
} from "../constants";
import { useProductsFilter } from "../context/ProductsFilterContext";
import { useProductsSearchCriteriaByURL } from "../hooks";
import { IProductsListingPageContent } from "../types";
import {
  changeProductsSearchCriteriaInURL,
  updateProductsSearchCriteriaBySelectedFilter,
} from "../utils";
import PricingFilter from "./PricingFilter";
import classes from "./StickyFacetsMenu.module.scss";

interface ToolbarFacetsMenuProps extends IProductsListingPageContent {}

export const StickyFacetsMenu = (props: ToolbarFacetsMenuProps) => {
  const { showMobileDrawerMenu, toggleMobileDrawerMenu } = useProductsFilter();

  const { availableFilters }: ISearchSliceInitialState = useAppSelector(
    (state) => state.search
  );
  const MenuItemFilters =
    availableFilters &&
    Array.isArray(availableFilters) &&
    availableFilters?.filter((obj: any) => obj?.type);

  const onClose = () => {
    toggleMobileDrawerMenu();
  };

  const renderItems = () => {
    if (MenuItemFilters)
      return MenuItemFilters?.map((item: any, index: number) => {
        return <MenuItem key={index} index={index} item={item} />;
      });
  };

  return (
    <aside
      className={clsx(
        classes["facets-menu"],
        showMobileDrawerMenu && classes["facets-menu--open"]
      )}
      data-testid={MOBILE_DRAWER_MENU_TEST_ID}
    >
      <div
        className={clsx(
          classes["facets-menu__flyout"],
          showMobileDrawerMenu && classes["facets-menu__flyout--open"]
        )}
      >
        <div className={classes["facets-menu__header"]}>
          {props["plp.page.filter"]}
          <button
            className={classes["facets-menu__close"]}
            onClick={onClose}
            data-testid={CLOSE_DRAWER_BUTTON_TEST_ID}
          >
            <Icon iconName="close-wider" />
          </button>
        </div>
        <div className={classes["facets-menu__filters"]}>{renderItems()}</div>
      </div>
    </aside>
  );
};

interface MenuItemProps {
  index: number;
  item: { name: string; type: string };
}
const singleSelectFacet = ["Thickness", "Grade", "Price"];

export const MenuItem = (props: MenuItemProps) => {
  const { index, item } = props;
  const {
    setSortBy,
    selectedSortBy,
    setFilterValues,
    activeDropdownFilter,
    selectedFilterValues,
    setActiveDropdownFilter,
  } = useProductsFilter();
  const router = useRouter();
  const searchCriteria = useProductsSearchCriteriaByURL();
  const [showMore, setShowMore] = useState(false);
  // const isActive = activeDropdownFilter === index;
  const isSortingBy = item.name === "Sort By";

  const isCheckBoxFilter = singleSelectFacet.includes(item.name) ? false : true;

  const onClick = () => {
    setActiveDropdownFilter(activeDropdownFilter !== index ? index : undefined);
  };

  const isItemChecked = (category: string, item: string) => {
    return isSortingBy
      ? selectedSortBy === item
      : selectedFilterValues?.[category]?.includes(item) ?? false;
  };
  const { availableFilters }: any = useAppSelector((state) => state.search);

  const hasRootFilter =
    selectedFilterValues?.[item.type]?.length &&
    availableFilters[1]?.initialFacets?.find(
      (el: any) => el.name === item.name
    );

  const currentFilter: any =
    availableFilters && hasRootFilter
      ? hasRootFilter
      : availableFilters?.find((el: any) => el.name === item.name);

  const currentItemsList =
    currentFilter?.items?.length > 10 && !showMore
      ? currentFilter?.items.slice(0, 5)
      : currentFilter?.items;

  const onSelectFilter = (category: string, value: string) => {
    if (isSortingBy) {
      const sortBy = selectedSortBy !== value ? value : undefined;

      setSortBy(sortBy);
      changeProductsSearchCriteriaInURL(router, { ...searchCriteria, sortBy });
    } else {
      const newSearchCriteria = updateProductsSearchCriteriaBySelectedFilter(
        searchCriteria,
        category,
        value,
        isCheckBoxFilter
      );

      setFilterValues({ category, value });
      changeProductsSearchCriteriaInURL(router, newSearchCriteria);
    }
  };
  const highlightSelectedFilter =
    selectedFilterValues?.[currentFilter.type]?.[0] === "custom"
      ? false
      : selectedFilterValues?.[currentFilter.type]?.length;

  return (
    <Accordion
      elevation={0}
      disableGutters={true}
      defaultExpanded={index >= 1 && index <= 3 ? true : false}
      className={classes["accordion-filter"]}
    >
      <AccordionSummary
        expandIcon={
          <Icon className={classes["icon"]} iconName="arrow-down-flexible" />
        }
        className={clsx(
          classes["search-facet"],
          highlightSelectedFilter && classes["search-facet-active"]
        )}
        onClick={onClick}
      >
        {item.name}
      </AccordionSummary>
      <AccordionDetails className={classes["search-facet-details"]}>
        {currentItemsList?.map((item: IFilterItem, subIndex: number) => (
          <label
            key={subIndex}
            className={
              classes[
                isSortingBy || !isCheckBoxFilter
                  ? "search-facet__sort"
                  : "search-facet__refinement"
              ]
            }
          >
            <input
              type="checkbox"
              className={classes["search-facet__input"]}
              name={item.name}
              value={item.value}
              onChange={() =>
                onSelectFilter(currentFilter?.type, item.value || "")
              }
              checked={isItemChecked(currentFilter?.type, item.value || "")}
            />
            <span
              className={
                classes[
                  isSortingBy || !isCheckBoxFilter
                    ? "search-facet__sort-label"
                    : "search-facet__label"
                ]
              }
            >
              {item?.name}
            </span>
          </label>
        ))}
        {currentFilter?.items?.length > 10 && (
          <button
            className={classes["facets-menu__show-more"]}
            onClick={() => setShowMore((toggle) => !toggle)}
          >
            {`Show ${showMore ? "less" : "more"}`}
          </button>
        )}
        {item.type === "price" && (
          <PricingFilter
            onSelectFilter={onSelectFilter}
            selectedValue={selectedFilterValues?.[item.type]}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};
